import { FileType } from 'interfaces/ia.interface.ts';
import { Nullable } from 'interfaces/Nullable.ts';
import { saveOfflineFile } from 'store/file_db/file_db.thunk.ts';
import moment from 'moment/moment';
import { findByQuery, putDocument } from 'store/db/db.thunk.ts';
import { setIADataSet } from 'store/ia/ia.actions.ts';
import { setPADataSet } from 'store/pa/pa.actions.ts';

export const saveFilesToPouchDB = async (
  uploadFiles: FileType[],
  pvGlobalGroupID: string,
  dispatch: any
) => {
  const localUploadedFiles: any[] = [];
  // try {
  for (let i = 0; i < uploadFiles.length; i++) {
    const file = uploadFiles[i];
    const file_name = file.name;
    const fileKey = '' + new Date().getTime() + '__' + file_name;
    await dispatch(
      saveOfflineFile({
        key: fileKey,
        file,
        pvGlobalGroupID,
        name: file.name,
        size: file.size,
      })
    );
    localUploadedFiles.push(file);
  }
  return localUploadedFiles;
  // } catch (error) {
  //   throw error;
  // }
};

//update pouch db item with response from sns
export const updatePouchDBItem = async (
  item: any,
  category: string,
  dispatch: any,
  userId: number
) => {
  item.dma_category = category;
  if (item?.utilities_out)
    item.utilities_out = item.utilities_out.split(',').map((item: any) => item.trim());
  if (item?.insurance_type)
    item.insurance_type = item.insurance_type.split(',').map((item: any) => item.trim());

  item.created_at = moment(item.created_at, 'YYYY-MM-DD HH:mm:ss').toISOString();
  item.updated_at = moment(item.updated_at, 'YYYY-MM-DD HH:mm:ss').toISOString();

  const foundDoc = (await findByQuery({
    selector: { pvGlobalID: item.pvGlobalID },
  })) as any;
  // `item` is the payload the server broadcast, so `item.user_id` is the id the
  // server stamped -- the local doc's own attribution is not part of this
  // comparison. A record created offline, which holds a null `user_id` until its
  // first sync adopts the stamped one, therefore cannot break the echo check.
  // The unsaved guard is evaluated first and still wins: a local edit in
  // progress is never overwritten by an incoming broadcast, whoever wrote it.
  if (foundDoc[0]?.isUnsaved || Number(item.user_id) === userId) {
    //item conflicts with unsaved local item or is same user
    return;
  } else {
    const updatedObject = foundDoc[0] ? { ...foundDoc[0], ...item } : item;
    await putDocument(updatedObject).then(async () => {
      const getItemsFromPouchDB = (await findByQuery({
        selector: { dma_category: category },
      })) as any;
      if (category === 'ia') {
        dispatch(setIADataSet(getItemsFromPouchDB));
      } else if (category === 'pa') {
        dispatch(setPADataSet(getItemsFromPouchDB));
      }
    });
  }
};

/**
 * The account id a DMA write stamped onto its response, or null when the
 * response carries none to adopt.
 *
 * A stamped id is always a positive safe integer: the API's identity middleware
 * rejects strings, non-integers, `0` and negatives before it writes the field,
 * so anything else arriving here means the response carried no id rather than a
 * strange one. The strictness is what makes "no id came back" a case the caller
 * can actually recognize, and so what lets it leave the local value alone
 * instead of replacing one guess with another.
 *
 *   happy:          POST response, `user_id` a positive integer -> that id
 *   nil/null:       PUT response with no record, or no `user_id` key -> null
 *   empty/zero:     `user_id` 0 or negative -> null, never adopted as an id
 *   upstream error: a rejected save never reaches here -- the caller checks the
 *                     dispatched action's type first -- so a failed write
 *                     cannot re-attribute a local record
 */
const readStampedUserId = (savedRecord: unknown): Nullable<number> => {
  if (!savedRecord || typeof savedRecord !== 'object') return null;
  const { user_id } = savedRecord as Record<string, unknown>;
  if (typeof user_id !== 'number' || !Number.isSafeInteger(user_id) || user_id <= 0) return null;
  return user_id;
};

/**
 * Whether a write response is the update endpoint's bare acknowledgement rather
 * than a saved record.
 *
 * `PUT /api/{ia,pa}[_state]/:id` answers with the database driver's update
 * result -- `{ success: true }` and nothing else -- where `POST` answers with the
 * saved row. Recognizing the shape is what lets the caller stay silent on the
 * routine edit path while still reporting a response that came back malformed.
 */
const isUpdateAcknowledgement = (savedRecord: unknown): boolean => {
  if (!savedRecord || typeof savedRecord !== 'object') return false;
  const keys = Object.keys(savedRecord as Record<string, unknown>);
  return keys.length === 1 && keys[0] === 'success';
};

/**
 * Copies the account id the server stamped onto a write response into the local
 * record, so the stored row and the local copy agree on who wrote it.
 *
 * The server is the authority here: every DMA write route overwrites the request
 * body's top-level `user_id` with the account id decoded from the caller's
 * verified ID token, so the value the client sent is a guess and the value that
 * comes back is the fact.
 *
 * A response carrying no id leaves `localItem` untouched. Overwriting attribution
 * with a guess would put back the divergence this exists to remove.
 *
 * Two different situations reach that branch and they are logged differently,
 * because only one of them is normal:
 *
 *   - An edit. `PUT` answers with the driver's update result (`{ success: true }`),
 *     never the saved record, so every edit sync lands here. Routine -- silent.
 *   - Anything else. A create whose response carried no id, or a save that
 *     resolved with no body at all, means the write did not come back in the
 *     shape this code expects. Warned, and named, because that is a real signal.
 *
 * Call it with an item that has already been prepared for storage; it copies
 * rather than mutates, and the caller writes the result.
 */
export const mergeServerAttribution = <
  T extends { pvGlobalID?: Nullable<string>; user_id?: Nullable<number> },
>(
  localItem: T,
  savedRecord: unknown
): T => {
  const stampedUserId = readStampedUserId(savedRecord);
  if (stampedUserId === null) {
    if (!isUpdateAcknowledgement(savedRecord)) {
      const reason =
        savedRecord === null || savedRecord === undefined
          ? 'the save resolved with no response body'
          : 'the response carried no usable user_id';
      console.warn(
        `mergeServerAttribution: ${reason} for ` +
          `${localItem.pvGlobalID ?? 'an unidentified record'}; local attribution left unchanged`
      );
    }
    return localItem;
  }
  // Authorship changed hands. Neither `logout()` nor `endSession()` clears the
  // assessment PouchDB, so a record created offline by one field worker can be
  // synced by whoever signs in next -- and the server attributes it to the
  // syncer. Adopting that id is correct, but it erases the only on-device trace
  // of who did the work, so say so before it goes.
  const priorUserId = localItem.user_id;
  if (typeof priorUserId === 'number' && priorUserId > 0 && priorUserId !== stampedUserId) {
    console.warn(
      `mergeServerAttribution: ${localItem.pvGlobalID ?? 'an unidentified record'} was created ` +
        `under account ${priorUserId} but the server recorded account ${stampedUserId} as its ` +
        'author; adopting the server value (shared device, or a different sign-in at sync time)'
    );
  }
  return { ...localItem, user_id: stampedUserId };
};
